import { getContext, track } from '../core';

/**
 * Known HubSpot form ID → semantic name mapping.
 *
 * Forms not in this map default to 'handraiser'.
 * On guide/webinar pages, form submissions also fire 'resource_download'.
 */
const FORM_IDS: Record<string, string> = {
  'b3303608-dae3-478e-a9df-e0da6f126611': 'newsletter',
  'ff267a22-862b-4879-99f8-02fcae4743f7': 'book-a-demo',
  '565c73b4-568d-4f67-8f6d-e349058b4b7e': 'pricing',
  '27dc3d1a-f074-4924-ac92-9d9320c642b8': 'event',
  '167368c3-4156-49f0-b83a-36e53ab64be5': 'webinar',
  'be1c8f09-024e-48e6-be44-d66d7a40384e': 'content',
  'e071d8e0-2d74-4519-942a-72f3874c53fb': 'roi-calculator',
};

const RESOURCE_PAGE_TYPES = ['guide', 'webinar'];

/**
 * HubSpot form that gates the product demo video on /watch-a-demo/.
 * Fires an additional `watch_demo_submit` event; the form stays a handraiser
 * (not in FORM_IDS) so its existing handraiser conversion tags keep firing.
 */
const WATCH_DEMO_FORM_ID = '6377615d-7c8f-4841-82fa-cb3603de0462';

interface HsFormCallbackData {
  type: 'hsFormCallback';
  eventName: string;
  id?: string;
  data?: {
    formGuid?: string;
  };
}

function isHsFormCallback(data: unknown): data is HsFormCallbackData {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).type === 'hsFormCallback'
  );
}

function getFormId(data: HsFormCallbackData): string {
  return data.id || data.data?.formGuid || '';
}

function getFormName(formId: string): string {
  return FORM_IDS[formId] || 'unknown';
}

function setupFormStartTracking(formId: string): void {
  // Attempt to attach focusin listener to the HubSpot form container.
  // Only works for inline-rendered forms (not cross-origin iframes).
  const formStartFired = new Set<string>();

  const tryAttach = () => {
    const forms = document.querySelectorAll<HTMLFormElement>('.hs-form');

    forms.forEach((form) => {
      if (form.dataset.trackingAttached) return;
      form.dataset.trackingAttached = 'true';

      form.addEventListener(
        'focusin',
        () => {
          const fid = form.querySelector<HTMLInputElement>('input[name="hs_context"]');
          const resolvedId = fid ? formId : formId;

          if (!formStartFired.has(resolvedId)) {
            formStartFired.add(resolvedId);
            const ctx = getContext();
            track('form_start', {
              form_id: resolvedId,
              page_name: ctx.page_title,
            });
          }
        },
        { once: true },
      );

      setupClientSideErrorTracking(form, formId);
    });
  };

  // Retry a few times since HS forms render asynchronously
  tryAttach();
  setTimeout(tryAttach, 1000);
  setTimeout(tryAttach, 3000);
}

function setupClientSideErrorTracking(form: HTMLFormElement, formId: string): void {
  const submitBtn = form.querySelector<HTMLElement>('.hs-button, input[type="submit"]');
  if (!submitBtn) return;

  let lastErrorTime = 0;

  submitBtn.addEventListener('click', () => {
    // Check after a short delay to allow HubSpot to render validation errors
    setTimeout(() => {
      const errors = form.querySelectorAll('.hs-error-msg, .hs-error-msgs li');
      if (errors.length > 0) {
        const now = Date.now();
        // Debounce: only fire once per 2 seconds to avoid spam on repeated clicks
        if (now - lastErrorTime < 2000) return;
        lastErrorTime = now;

        const ctx = getContext();
        track('form_error', {
          form_id: formId,
          page_name: ctx.page_title,
        });
      }
    }, 300);
  });
}

function handleFormSubmitted(formId: string): void {
  const ctx = getContext();
  const formName = getFormName(formId);

  // Always fire generic form_submission
  track('form_submission', {
    form_id: formId,
    form_name: formName,
    page_name: ctx.page_title,
  });

  // Fire specific events based on form ID
  switch (formName) {
    case 'newsletter':
      track('newsletter_signup', { form_id: formId, page_name: ctx.page_title });
      break;
    case 'book-a-demo':
      track('book_a_demo_submit', { form_id: formId, page_name: ctx.page_title });
      break;
    case 'pricing':
    case 'roi-calculator':
      track('pricing_form_submit', { form_id: formId, page_name: ctx.page_title });
      break;
  }

  // Fire resource_download on guide/webinar pages
  if (RESOURCE_PAGE_TYPES.includes(ctx.page_type)) {
    track('resource_download', {
      form_id: formId,
      resource_type: ctx.page_type,
      page_name: ctx.page_title,
    });
  }

  if (ctx.page_type === 'webinar') {
    track('webinar_register_now', {
      webinar_title: ctx.article_title || ctx.page_title,
      page_name: ctx.page_title,
    });
  }

  // Everything else that isn't newsletter or a known specific form → handraiser
  if (formName === 'unknown') {
    track('handraiser_submission', { form_id: formId, page_name: ctx.page_title });
  }

  // Watch-a-demo gate — fire in addition to the handraiser above.
  if (formId === WATCH_DEMO_FORM_ID) {
    track('watch_demo_submit', { form_id: formId, page_name: ctx.page_title });
  }
}

export function initHubSpotFormTracking(): void {
  window.addEventListener('message', (event: MessageEvent) => {
    if (!isHsFormCallback(event.data)) return;

    const formId = getFormId(event.data);
    const ctx = getContext();

    switch (event.data.eventName) {
      case 'onFormReady':
        if (formId) {
          setupFormStartTracking(formId);
        }
        break;

      case 'onFormSubmitError':
        track('form_error', {
          form_id: formId,
          page_name: ctx.page_title,
        });
        break;

      case 'onFormSubmitted':
        handleFormSubmitted(formId);
        break;
    }
  });
}
